import { useMemo } from 'react';

import type {
  Money,
  GetFlightSummarySelectedTotalPriceQuery,
} from '@/generated/graphql';

interface SummaryPrice {
  pricePerPassenger?: Money;
  nonDiscountedPricePerPassenger?: Money;
  nonDiscountedPrice?: Money;
}

const isValidMoney = (money?: Money): boolean =>
  money !== undefined &&
  typeof money.amount === 'number' &&
  typeof money.currency === 'string' &&
  money.currency.trim() !== '';

const extractValidMoney = (
  key:
    | 'pricePerPassenger'
    | 'nonDiscountedPricePerPassenger'
    | 'nonDiscountedPrice',
  source?: GetFlightSummarySelectedTotalPriceQuery['getSummaryPrice']
): Money | undefined => {
  const value = source?.[key];
  return value && isValidMoney(value) ? value : undefined;
};

const useSummaryPrice = (
  getSummaryPrice:
    | GetFlightSummarySelectedTotalPriceQuery['getSummaryPrice']
    | undefined
): SummaryPrice => {
  return useMemo(() => {
    const pricePerPassenger = extractValidMoney(
      'pricePerPassenger',
      getSummaryPrice
    );
    const nonDiscountedPricePerPassenger = extractValidMoney(
      'nonDiscountedPricePerPassenger',
      getSummaryPrice
    );
    const nonDiscountedPrice = extractValidMoney(
      'nonDiscountedPrice',
      getSummaryPrice
    );
    return {
      pricePerPassenger,
      nonDiscountedPricePerPassenger,
      nonDiscountedPrice,
    };
  }, [getSummaryPrice]);
};

export default useSummaryPrice;
